import { useToast } from 'lib/hooks';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ABORT_ERROR_NAME } from '../constants';
import {
  ICompleteMultipartUploadResponse,
  IS3UploadAdapter,
  useS3Uploader,
} from '../hooks';
import { ISingleFileUploadProps, SingleFileUpload } from './SingleFileUpload';

export interface IS3SingleFileUploadV2Props
  extends Omit<ISingleFileUploadProps, 'onChange'> {
  adapter: IS3UploadAdapter;
  onAfterUpload?: () => void;
  onRemove?: () => void;
  onBeforeUpload?: () => void;
  onSuccess: (props: ICompleteMultipartUploadResponse<unknown>) => void;
  renderAfter?: (props: {
    abortUpload: () => void;
    uploadPercentage: number;
  }) => ReactElement;
  // TODO remove
  __startFile: File;
  __onChange: (file: File) => void;
}

export const S3SingleFileUploadV2 = ({
  accept,
  adapter,
  children,
  className,
  errorMessage,
  errorMessageClassName,
  file: passedFile,
  isDisabled,
  isInvalid,
  label,
  onAfterUpload,
  onBeforeUpload,
  onRemove,
  onSuccess,
  previewUrl,
  renderAfter,
  // TODO remove
  __startFile,
  __onChange,
}: IS3SingleFileUploadV2Props) => {
  const { message } = useToast();

  const passedFileRef = useRef<File>(passedFile);

  const [file, setFile] = useState<File>(passedFile);
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const resetFile = () => setFile(passedFile);

  const { uploadFile, abortUpload } = useS3Uploader({
    accept,
    adapter,
    onProgress: setUploadPercentage,
  });

  // If the `passedFile` gets updated (let's say because it loads async), we
  // need to update the file (similar to `enableReinitialize` in Formik). It's
  // fine to use name here because deep equality check is not possible and
  // `passedFile` names should never match.
  useEffect(() => {
    if (passedFileRef.current?.name !== passedFile?.name) {
      passedFileRef.current = passedFile;
      setFile(passedFile);
    }
  }, [passedFile]);

  // TODO remove
  useEffect(() => {
    if (__startFile) {
      __handleUpload();
    }
  }, [__startFile]);

  // TODO uncomment
  // const handleChangeFile = async (changedFile: File) => {
  //   if (!changedFile) {
  //     handleRemoveFile();
  //     return;
  //   }
  //   setFile(changedFile);
  //   onBeforeUpload?.();
  //   try {
  //     const res = await uploadFile(changedFile);
  //     onSuccess(res);
  //     message.success('File successfully uploaded');
  //   } catch (e) {
  //     if (e.name !== ABORT_ERROR_NAME) {
  //       message.error(e.message ?? 'Upload failed, please try again');
  //     }
  //     resetFile();
  //   }
  //   onAfterUpload?.();
  // };

  // TODO remove
  const __handleUpload = async () => {
    if (!file) {
      return;
    }
    onBeforeUpload?.();
    try {
      const res = await uploadFile(file);
      onSuccess(res);
      message.success('File successfully uploaded');
    } catch (e) {
      if (e.name !== ABORT_ERROR_NAME) {
        message.error(e.message ?? 'Upload failed, please try again');
      }
      resetFile();
    }
    onAfterUpload?.();
  };

  // TODO remove
  const __handleChangeFile = async (changedFile: File) => {
    if (!changedFile) {
      handleRemoveFile();
      return;
    }
    setFile(changedFile);
    __onChange(changedFile);
  };

  const handleRemoveFile = () => {
    setFile(undefined);
    onRemove?.();
  };

  return (
    <>
      <SingleFileUpload
        accept={accept}
        className={className}
        errorMessage={errorMessage}
        errorMessageClassName={errorMessageClassName}
        file={file}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        label={label}
        onChange={__handleChangeFile}
        previewUrl={previewUrl}
      >
        {children}
      </SingleFileUpload>
      {renderAfter?.({ abortUpload, uploadPercentage })}
    </>
  );
};
